import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CostCenter } from "../models/CostCenter";
import {
	removeCostCenter,
	upsertCostCenter,
} from "../repositories/CostCentersRepository";
import { COST_CENTERS_QUERY_KEYS } from "./useCostCenters";

interface MutationOptions {
	onSuccess?: (data?: CostCenter) => void;
	onError?: (error: Error) => void;
}

export const useUpsertCostCenter = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (costCenter: CostCenter) => {
			return await upsertCostCenter(costCenter);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: COST_CENTERS_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertCostCenter: mutation.mutate,
		upsertCostCenterAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteCostCenter = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeCostCenter(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: COST_CENTERS_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteCostCenter: mutation.mutate,
		deleteCostCenterAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
